/* eslint-disable react-hooks/exhaustive-deps */

import { Box } from '@zoopla-eng/zds-components/box';
import { Button, ButtonLink } from '@zoopla-eng/zds-components/button';
import { Checkbox } from '@zoopla-eng/zds-components/checkbox';
import { InputField, Textarea } from '@zoopla-eng/zds-components/input-field';
import { Link } from '@zoopla-eng/zds-components/link';
import { Select } from '@zoopla-eng/zds-components/select';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { triggerTracking } from '@zoopla/tracking';
import { FC, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { AgentsOptionsSelector } from './AgentsSelection';
import {
    agentSelectionPlusCheckboxWrapperStyle,
    agentSelectionWrapperStyle,
    desktopDescriptionStyle,
    desktopHeadingStyle,
    emailFormContainerStyle,
    emailHeaderStyle,
    errorTextStyle,
    fieldWrapperStyle,
    footerStyle,
    formFieldsWrapperStyle,
    freeAgentValuationCheckboxWrapperStyle,
    innerFormWrapperStyle,
    loggedOutSuccessWrapperStyle,
    mobileDescriptionStyle,
    mobileHeadingStyle,
    successTextStyle,
    successWrapperStyle,
    valuationTextStyle,
    wrapperStyle
} from './EmailAgents.styles.css';
import { EmailAgentsProps, EmailAllOption } from './EmailAgents.types';
import {
    getEmailFormField,
    patternErrorMessages,
    requiredErrorMessages
} from './hooks/useEmailAll';

const NAME_VALIDATION_FALLBACK_PATTERN =
    /^\s*?(?:\w{2,}(?:\w|[-'.])*)\s+(?:\w{2,}(?:\w|[-'.])*\s*?)+$/;
const EMAIL_VALIDATION_FALLBACK_PATTERN =
    /^[a-zA-Z0-9_][a-zA-Z0-9.+_-]*[a-zA-Z0-9_]@[a-zA-Z0-9-]+(?:.[a-zA-Z0-9-]+)+$/;
const PHONE_VALIDATION_FALLBACK_PATTERN =
    /^(?:\d|\s|(?:[-+()]))*(\d(?:\d|\s|(?:[-+()]))*){7,}$/;

const statusOptions: { [k: string]: string } = {
    pleaseSelect: 'Please select',
    firstTimeBuyer: 'I am a first-time buyer',
    investor: "I am an investor/I'm hoping to invest",
    noPropertyToSell: 'I do not have a property to sell',
    propertyOffer: 'I have an offer on my property',
    other: 'Other',
    rentProperty: 'I have a property to let',
    sellProperty: 'I have a property to sell',
    recentlySold: 'I have recently sold'
};

export const EmailAgents: FC<React.PropsWithChildren<EmailAgentsProps>> = ({
    searchType,
    location,
    isUserLoggedIn,
    createAlert,
    emailAllState,
    singleColumnLayout = false,
    successCloseHandler
}) => {
    const {
        emailAll,
        formHandlers: {
            handleSubmit,
            formState: { errors },
            register,
            control,
            watch
        },
        onSubmit,
        isError,
        isLoading,
        eventCategory
    } = emailAllState;

    const freeValuation = watch('getFreeValuation', false);

    useEffect(() => {
        emailAll.result?.trackingObjects?.forEach((trackingObject) => {
            triggerTracking({
                event: 'GAEventEcommerce',
                ...trackingObject
            });
        });

        if (freeValuation) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: eventCategory,
                eventAction: 'selected',
                eventLabel: 'free_valuation_optin'
            });
        }
    }, [emailAll.result]);

    const nameField = getEmailFormField('name', emailAll.form);
    const emailField = getEmailFormField('email', emailAll.form);
    const phoneField = getEmailFormField('phone', emailAll.form);
    const statusField = getEmailFormField('status', emailAll.form);
    const messageField = getEmailFormField('message', emailAll.form);

    const fieldValidationConfig = (
        fieldName: string,
        validation: string | null | undefined,
        fallbackValidation: RegExp
    ) => ({
        required: requiredErrorMessages[fieldName],
        pattern: {
            value: validation ? new RegExp(validation) : fallbackValidation,
            message: patternErrorMessages[fieldName]
        }
    });

    const hasSubmittedEmailAll = emailAll.result;
    const emailAllExists = emailAll.form || hasSubmittedEmailAll;

    const statusFieldOptions = statusField?.options || [];
    const options = statusFieldOptions?.map((statusOption: EmailAllOption) => {
        const { value, disabled, i18NLabelKey } = statusOption;
        return {
            // If statusOption is disabled it takes an empty value to ensure
            // that the required error is shown if a user selects this option
            id: !disabled && value ? value : '',
            text: statusOptions[i18NLabelKey ?? '']
        };
    });

    if (!emailAllExists) {
        return null;
    }

    const desktopDescription = `Send a message to agents and developers matching your search for property ${
        searchType === 'for-sale' ? 'for sale' : 'to rent'
    } in ${location} `;

    const agentCount = emailAll.result?.branches?.length || 1;
    const successTitle =
        agentCount < 2
            ? `Success! You’ve sent your message to ${agentCount} estate agent.`
            : `Success! You’ve sent your message to ${agentCount} estate agents.`;
    const successText1 = 'Check your inbox for a confirmation email.';
    const successText2 =
        'You should hear back from your chosen agents within the next couple of days.';

    return (
        <form
            className={wrapperStyle}
            onSubmit={handleSubmit(onSubmit)}
            data-testid="email-all-agents-form"
        >
            <div className={emailFormContainerStyle}>
                <div className={emailHeaderStyle}>
                    {!hasSubmittedEmailAll ? (
                        <>
                            {/* Mobile and desktop have different copy */}
                            <div
                                className={desktopHeadingStyle({
                                    multiColumnLayout: !singleColumnLayout
                                })}
                            >
                                <h3 className={fontGroups.mug}>
                                    Contact relevant estate agents and new homes
                                    developers
                                </h3>
                            </div>
                            <p
                                className={`${desktopDescriptionStyle({
                                    multiColumnLayout: !singleColumnLayout
                                })} ${fontGroups.ladle}`}
                            >
                                {desktopDescription}
                            </p>
                            <div
                                className={`${mobileHeadingStyle({
                                    multiColumnLayout: !singleColumnLayout
                                })} ${fontGroups.ladle}`}
                            >
                                <h3 className={fontGroups.mug}>
                                    Can&apos;t find what you&apos;re looking
                                    for?
                                </h3>
                            </div>
                            <p
                                className={mobileDescriptionStyle({
                                    multiColumnLayout: !singleColumnLayout
                                })}
                            >
                                Boost your property search by filling out the
                                form below, and sending it to local agents of
                                your choice.
                            </p>
                        </>
                    ) : (
                        <div className={successWrapperStyle}>
                            <Box pb="S400">
                                <h1
                                    className={fontGroups.mug}
                                    data-testid="success-page-title"
                                >
                                    {successTitle}
                                </h1>
                            </Box>
                            <p
                                className={successTextStyle}
                                data-testid="success-page-text"
                            >
                                {successText1}
                                <br />
                                {successText2}
                            </p>
                            {successCloseHandler && (
                                <Box pt="S400" pb="M100">
                                    <Button
                                        category="primary"
                                        data-testid="success-close"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            successCloseHandler();
                                        }}
                                    >
                                        OK
                                    </Button>
                                </Box>
                            )}

                            {freeValuation && (
                                <>
                                    <p
                                        className={`${valuationTextStyle} ${fontGroups.ladle}`}
                                        data-testid="success-page-free-evaluation-text"
                                    >
                                        To receive your free, no-obligation
                                        valuation of your home simply complete
                                        our short valuation form.
                                    </p>

                                    <Box pt="S400">
                                        <ButtonLink
                                            category="primary"
                                            href="/valuation/"
                                            data-testid="free-evaluation"
                                        >
                                            Get a free valuation
                                        </ButtonLink>
                                    </Box>
                                </>
                            )}
                        </div>
                    )}
                </div>
                {!hasSubmittedEmailAll ? (
                    <div
                        className={innerFormWrapperStyle({
                            multiColumnLayout: !singleColumnLayout
                        })}
                    >
                        <div
                            className={formFieldsWrapperStyle({
                                multiColumnLayout: !singleColumnLayout
                            })}
                        >
                            <div className={fieldWrapperStyle}>
                                <InputField
                                    defaultValue={nameField?.value || ''}
                                    label="Full name"
                                    errorText={errors.name?.message}
                                    {...register(
                                        'name',
                                        fieldValidationConfig(
                                            'name',
                                            nameField?.validationData?.pattern,
                                            NAME_VALIDATION_FALLBACK_PATTERN
                                        )
                                    )}
                                />
                            </div>
                            <div className={fieldWrapperStyle}>
                                <InputField
                                    defaultValue={emailField?.value || ''}
                                    label="Email address"
                                    errorText={errors.email?.message}
                                    {...register(
                                        'email',
                                        fieldValidationConfig(
                                            'email',
                                            emailField?.validationData?.pattern,
                                            EMAIL_VALIDATION_FALLBACK_PATTERN
                                        )
                                    )}
                                />
                            </div>
                            <div className={fieldWrapperStyle}>
                                <InputField
                                    defaultValue={phoneField?.value || ''}
                                    label="Phone number"
                                    errorText={errors.phone?.message}
                                    {...register(
                                        'phone',
                                        fieldValidationConfig(
                                            'phone',
                                            phoneField?.validationData?.pattern,
                                            PHONE_VALIDATION_FALLBACK_PATTERN
                                        )
                                    )}
                                />
                            </div>
                            {!!options.length && (
                                <div className={fieldWrapperStyle}>
                                    <Controller
                                        control={control}
                                        name="status"
                                        rules={{
                                            required:
                                                'Please select the option that best reflects your status'
                                        }}
                                        render={({
                                            field: { onChange, value }
                                        }) => (
                                            <>
                                                {value}
                                                <Select
                                                    label="Your situation"
                                                    options={options}
                                                    errorText={
                                                        errors.status?.message
                                                    }
                                                    selectedOption={
                                                        options.find(
                                                            (option) =>
                                                                option.id ===
                                                                value
                                                        ) ?? options[0]
                                                    }
                                                    onSelectedOptionChange={(
                                                        option
                                                    ) => {
                                                        onChange(option.id);
                                                    }}
                                                />
                                            </>
                                        )}
                                    />
                                </div>
                            )}
                            <div className={fieldWrapperStyle}>
                                <Textarea
                                    defaultValue={messageField?.value || ''}
                                    errorText={errors.message?.message}
                                    placeholder="e.g. I am looking for property for sale within 30 miles radius of London Bridge Station for £180,000 or less. Please let me know about any suitable properties you have. Thanks."
                                    label="Your message (optional)"
                                    // P5ZDS unify maximum character counts
                                    helpText="Max. 500 characters"
                                    maxLength={500}
                                    {...register('message')}
                                />
                            </div>
                        </div>

                        <div
                            className={agentSelectionPlusCheckboxWrapperStyle({
                                multiColumnLayout: !singleColumnLayout
                            })}
                        >
                            <div className={agentSelectionWrapperStyle}>
                                <Controller
                                    control={control}
                                    name="branches"
                                    rules={{ required: true }}
                                    render={({ field: { onChange } }) => (
                                        <AgentsOptionsSelector
                                            key="agent-options"
                                            branches={emailAll.form?.branches}
                                            validationError={!!errors.branches}
                                            singleColumnLayout={
                                                singleColumnLayout
                                            }
                                            onChange={onChange}
                                        />
                                    )}
                                />
                            </div>
                            <div
                                className={
                                    freeAgentValuationCheckboxWrapperStyle
                                }
                            >
                                <Controller
                                    name="getFreeValuation"
                                    control={control}
                                    render={({
                                        field: { onChange, value, name }
                                    }) => (
                                        <Checkbox
                                            name={name}
                                            label="Get a free agent valuation of my property"
                                            onCheckedChange={(checked) => {
                                                onChange(!!checked);
                                            }}
                                            checked={value}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    !isUserLoggedIn &&
                    createAlert && (
                        <div className={loggedOutSuccessWrapperStyle}>
                            <h1
                                className={fontGroups.mug}
                                data-testid="success-additional-heading"
                            >
                                Interested in buying a house?
                            </h1>
                            <p data-testid="success-additional-text">
                                <Link
                                    data-testid="success-additional-link"
                                    href={createAlert ?? ''}
                                >
                                    Create an email alert from your search terms
                                </Link>
                                {' to find out when new properties are listed.'}
                            </p>
                        </div>
                    )
                )}
                <div>
                    {isError && (
                        <p className={errorTextStyle} role="alert">
                            Something went wrong. Please correct the errors.
                        </p>
                    )}
                </div>
                {!hasSubmittedEmailAll && (
                    <div
                        className={footerStyle({
                            multiColumnLayout: !singleColumnLayout
                        })}
                    >
                        <p>
                            By sending this form, your enquiry will be sent to
                            the selected agents and developers and they will be
                            in touch with you (for more information, please see
                            our{' '}
                            <Link
                                key="privacyPolicy"
                                data-testid="privacy-link"
                                href="/privacy"
                                target="_blank"
                            >
                                Privacy Policy
                            </Link>
                            ).
                        </p>
                        <Button
                            category="primary"
                            disabled={isLoading}
                            fw
                            type="submit"
                            loading={isLoading}
                            loadingText="Sending..."
                        >
                            Send Message
                        </Button>
                    </div>
                )}
            </div>
        </form>
    );
};
